import { useEffect, useRef, useState } from 'react';
import type { PillTone } from '@/components/StatusPill';
import { getGeneration, isGenerationFinished, type ScenarioGeneration } from '@/api/scenarioIntelligence';

// Shared by the Generate and Proposals drawers (SI-05a).

/** How often a running generation is re-read. It is a background job; the drawer may close at any time. */
export const POLL_MS = 2500;

export const STAGE_TONE: Record<string, PillTone> = {
  Done: 'green',
  Running: 'ink',
  Pending: 'slate',
  Failed: 'red',
  Skipped: 'slate',
  Later: 'slate',
};

/** A fresh idempotency key: a retry with the same key returns the same generation instead of starting a second one. */
export function newRequestId(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c?.randomUUID) return c.randomUUID();
  return `req-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Follows one generation until it finishes: re-reads it every {@link POLL_MS} while it is queued or running, and stops on
 * a finished state, on unmount, or when the key or practice changes. A late answer for an older key is ignored.
 */
export function usePollGeneration(key: string | null, merchantId: number | null, seed: ScenarioGeneration | null): ScenarioGeneration | null {
  const [generation, setGeneration] = useState<ScenarioGeneration | null>(seed);
  const seq = useRef(0);

  useEffect(() => {
    const mine = ++seq.current;
    setGeneration(seed);
    if (!key || isGenerationFinished(seed)) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = () => {
      getGeneration(key, merchantId)
        .then((g) => {
          if (mine !== seq.current) return;
          setGeneration(g);
          if (!isGenerationFinished(g)) timer = setTimeout(tick, POLL_MS);
        })
        .catch(() => {
          if (mine === seq.current) timer = setTimeout(tick, POLL_MS * 2); // a missed read is retried, never shown as a failure
        });
    };
    timer = setTimeout(tick, POLL_MS);
    return () => {
      seq.current++;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, merchantId, seed?.key]);

  return generation;
}
